function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays dynamic per-item pricing based on quantity and volume pricing tiers.
 * Updates automatically when quantity changes or cart is updated.
 *
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {string} price - Formatted price string (e.g., "$9.50 USD")
 *
 * @typedef {Object} PricePerItemRefs
 * @property {HTMLElement} [pricePerItemText] - The text element displaying the price
 *
 * @extends {Component<PricePerItemRefs>}
 */
var _priceBreaks = _classPrivateFieldLooseKey("priceBreaks");
var _abortController = _classPrivateFieldLooseKey("abortController");
var _parsePriceBreaks = _classPrivateFieldLooseKey("parsePriceBreaks");
var _attachEventListeners = _classPrivateFieldLooseKey("attachEventListeners");
var _handleQuantityUpdate = _classPrivateFieldLooseKey("handleQuantityUpdate");
var _handleCartUpdate = _classPrivateFieldLooseKey("handleCartUpdate");
var _getCurrentQuantity = _classPrivateFieldLooseKey("getCurrentQuantity");
var _updatePriceDisplay = _classPrivateFieldLooseKey("updatePriceDisplay");
class PricePerItemComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Gets the total quantity (cart + current input value)
     * @returns {number}
     */
    Object.defineProperty(this, _getCurrentQuantity, {
      value: _getCurrentQuantity2
    });
    /**
     * Attaches event listeners for quantity and cart updates
     */
    Object.defineProperty(this, _attachEventListeners, {
      value: _attachEventListeners2
    });
    /**
     * Parses price breaks from data attributes
     */
    Object.defineProperty(this, _parsePriceBreaks, {
      value: _parsePriceBreaks2
    });
    /** @type {PriceBreak[]} */
    Object.defineProperty(this, _priceBreaks, {
      writable: true,
      value: []
    });
    Object.defineProperty(this, _abortController, {
      writable: true,
      value: new AbortController()
    });
    /**
     * Handles quantity selector updates
     * @param {Event} event
     */
    Object.defineProperty(this, _handleQuantityUpdate, {
      writable: true,
      value: event => {
        // Only respond to updates for our variant's quantity selector
        const form = this.closest('product-form-component');
        if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
        _classPrivateFieldLooseBase(this, _updatePriceDisplay)[_updatePriceDisplay]();
      }
    });
    /**
     * Handles cart updates by refreshing display
     */
    Object.defineProperty(this, _handleCartUpdate, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _updatePriceDisplay)[_updatePriceDisplay]();
      }
    });
    /**
     * Private wrapper for event handlers
     */
    Object.defineProperty(this, _updatePriceDisplay, {
      writable: true,
      value: () => {
        this.updatePriceDisplay();
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    _classPrivateFieldLooseBase(this, _parsePriceBreaks)[_parsePriceBreaks]();
    _classPrivateFieldLooseBase(this, _attachEventListeners)[_attachEventListeners]();
    _classPrivateFieldLooseBase(this, _updatePriceDisplay)[_updatePriceDisplay]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abortController)[_abortController].abort();
  }
  /**
   * Updates the price display based on current quantity
   */
  updatePriceDisplay() {
    if (!_classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].length || !this.refs.pricePerItemText) return;
    const quantity = _classPrivateFieldLooseBase(this, _getCurrentQuantity)[_getCurrentQuantity]();

    // Price breaks are sorted descending, find first tier that quantity qualifies for
    const priceBreak = _classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].find(pb => quantity >= pb.quantity) ?? _classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks][_classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].length - 1];
    if (priceBreak) {
      this.refs.pricePerItemText.innerHTML = `${this.dataset.atText} ${priceBreak.price}/${this.dataset.eachText}`;
    }
  }
}
function _parsePriceBreaks2() {
  const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
  const {
    variantPrice,
    priceBreaks: priceBreaksData
  } = this.dataset;

  // Start with base price tier
  if (variantPrice) {
    _classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].push({
      quantity: minQuantity,
      price: variantPrice
    });
  }

  // Parse additional price breaks from JSON array
  if (priceBreaksData) {
    const breaks = JSON.parse(priceBreaksData);
    for (const {
      quantity,
      price
    } of breaks) {
      if (quantity && price) {
        _classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].push({
          quantity: parseInt(quantity),
          price
        });
      }
    }
  }

  // Sort by quantity descending for efficient lookup
  _classPrivateFieldLooseBase(this, _priceBreaks)[_priceBreaks].sort((a, b) => b.quantity - a.quantity);
}
function _attachEventListeners2() {
  const {
    signal
  } = _classPrivateFieldLooseBase(this, _abortController)[_abortController];

  // Listen on document to catch all events (more reliable than form-only)
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, _classPrivateFieldLooseBase(this, _handleQuantityUpdate)[_handleQuantityUpdate], {
    signal
  });
  document.addEventListener(ThemeEvents.cartUpdate, _classPrivateFieldLooseBase(this, _handleCartUpdate)[_handleCartUpdate], {
    signal
  });
}
function _getCurrentQuantity2() {
  const form = this.closest('product-form-component');
  const quantityInput = /** @type {HTMLInputElement | null} */form?.querySelector('input[name="quantity"]');
  if (!quantityInput) return 1;

  // Read the current cart quantity from the data attribute
  const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0') || 0;
  // Read the current input value (quantity to add)
  const inputQty = parseInt(quantityInput.value) || 1;
  return cartQty + inputQty;
}
if (!customElements.get('price-per-item')) {
  customElements.define('price-per-item', PricePerItemComponent);
}
